import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  size,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useListNavigation,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import clsx from 'clsx';
import { isEqual } from 'lodash-es';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ForwardedRef,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { ActionRow, Option as OptionType, SelectProps } from './types';

import { useEscapePriorityDismiss } from '../../utils/floatingDismissPriority';
import { resolveTopLayerRoot } from '../../utils/resolveTopLayerRoot';
import { typedForwardRef } from '../../utils/typedForwardRef';
import { typedMemo } from '../../utils/typedMemo';
import { Chip } from '../Chip/Chip';
import { Icon } from '../Icon/Icon';
import { Text } from '../Text/Text';
import { TextBlock } from '../TextBlock/TextBlock';
import { useListboxState, useStableCallback } from './hooks';
import './Select.css';
import { Listbox } from './subcomponents/Listbox';
import {
  filterOptionGroups,
  flattenOptions,
  flattenOptionGroups,
  getListboxDisabledIndices,
  getSelectedValues,
  isMultiSelect,
  resolveSelectedOptions,
  stringifyOptionValue,
} from './utils';

const messages = defineMessages({
  addOption: {
    id: 'eds.select.addOption',
    defaultMessage: 'Create {query}',
    description:
      'Action at the bottom of the Select menu that creates an option from the typed text',
  },
  loading: {
    id: 'eds.select.loading',
    defaultMessage: 'Loading options',
    description:
      'Announced while the Select menu waits for the options it is fetching',
  },
  loadMore: {
    id: 'eds.select.loadMore',
    defaultMessage: 'Load More',
    description:
      'Row at the bottom of the Select menu that fetches the next page of options',
  },
});

export const Select = typedMemo(
  typedForwardRef(function Select<V = string>(
    props: SelectProps<V>,
    forwardedRef: ForwardedRef<HTMLInputElement>,
  ) {
    const {
      label,
      name,
      options,
      disabled,
      isLoading,
      isLoadingMore,
      menuMinWidth,
      noResultsText,
      placeholder,
      required,
      status,
      onAddOption,
      onLoadMore,
      onQueryChange,
      __private,
    } = props;
    const { ariaDescribedBy, ariaLabelledBy, id, seamless } = __private ?? {};
    const ariaLabel = ariaLabelledBy ? undefined : label;

    const intl = useIntl();
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const fallbackListId = useId();
    const inputRef = useRef<HTMLInputElement | null>(null);
    // `useFloating` doesn't refresh a middleware's closure just because a
    // render passed it new inline config — read the latest value through a
    // ref instead of closing over `menuMinWidth` directly in `apply`.
    const menuMinWidthRef = useRef(menuMinWidth);
    menuMinWidthRef.current = menuMinWidth;
    const queryRef = useRef(query);

    // The server already matched the query; matching again here would drop
    // results it returned on terms this filter cannot see, such as an id.
    const isServerFiltered = Boolean(onQueryChange);

    // Dedupes against `queryRef` rather than `query`: selecting an option clears
    // the query and then closes the menu, which clears it again in the same tick.
    const setQueryState = useCallback(
      (next: string) => {
        setQuery(next);
        if (queryRef.current === next) return;
        queryRef.current = next;
        onQueryChange?.(next);
      },
      [onQueryChange],
    );

    const effectiveQuery = isServerFiltered ? '' : query;

    const filteredGroups = useMemo(
      () => filterOptionGroups(options, effectiveQuery),
      [effectiveQuery, options],
    );
    // Keyboard navigation, the active index, and the selected lookup are all flat;
    // only the rendering below cares about the group boundaries.
    const filteredOptions = useMemo(
      () => flattenOptionGroups(filteredGroups),
      [filteredGroups],
    );
    const allOptions = useMemo(() => flattenOptions(options), [options]);

    const trimmedQuery = query.trim();
    // Compared exactly rather than through `normalizeSearchText`: that folding
    // exists to match a query against options, and identity is a stricter
    // question. Reusing it here reads `test` as a duplicate of `TEST` and leaves
    // no way to add the value that was actually typed.
    const canAddOption =
      Boolean(onAddOption) &&
      trimmedQuery.length > 0 &&
      !allOptions.some((option) => option.text.trim() === trimmedQuery);
    const addOptionText = intl.formatMessage(messages.addOption, {
      query: trimmedQuery,
    });
    const loadMoreText = intl.formatMessage(messages.loadMore);
    const hasOptions = filteredOptions.length > 0;
    const hasMenuContent = hasOptions || canAddOption;
    // Only options suppress the placeholders. Create row is an action,
    // and sits under them while a request is out.
    const showLoadingPlaceholders = Boolean(isLoading) && !hasOptions;
    // Nothing to load more of yet while the first set is still on its way.
    const canLoadMore = Boolean(onLoadMore) && !showLoadingPlaceholders;

    const {
      activeIndex,
      elementsRef,
      isKeyboardModality,
      isOptionSelected,
      listNavigation: listNavigationOptions,
      markKeyboardInput,
      markPointerInput,
      resetNavigation,
      setActiveIndex,
      setIsKeyboardModality,
      onHoverIndex,
      onSelectOption,
    } = useListboxState({ options: filteredOptions, selection: props });

    const handleOpenChange = useCallback(
      (open: boolean) => {
        if (open) {
          setPortalRoot(resolveTopLayerRoot(inputRef.current));
        }
        setIsOpen(open);
        if (!open) {
          setQueryState('');
          resetNavigation();
        }
      },
      [resetNavigation, setQueryState],
    );

    // Resolved when the listbox opens: the trigger is mounted by then, and the
    // host can change between openings (a dialog closing, a popover reused).
    const [portalRoot, setPortalRoot] = useState<HTMLElement | undefined>();

    // Joins an ancestor `Modal`'s `FloatingTree` when opened nested inside one,
    // so Escape closes this dropdown first without also closing the modal.
    // Resolves its parent from context automatically; a no-op standalone.
    const nodeId = useFloatingNodeId();

    const { floatingStyles, refs, context } = useFloating<HTMLInputElement>({
      nodeId,
      middleware: [
        offset({ mainAxis: 4, alignmentAxis: 0 }),
        flip(),
        shift(),
        size({
          apply({ rects, elements }) {
            Object.assign(elements.floating.style, {
              width: `${Math.max(rects.reference.width, menuMinWidthRef.current ?? 0)}px`,
            });
          },
        }),
      ],
      open: isOpen,
      placement: 'bottom-start',
      // A top-layer host clips the portalled listbox when it hides its own
      // overflow; fixed positioning escapes that without leaving the layer.
      strategy: 'fixed',
      whileElementsMounted: autoUpdate,
      onOpenChange: handleOpenChange,
    });
    const listId = context.floatingId ?? fallbackListId;

    const dismiss = useEscapePriorityDismiss(context, {
      // Reference is the input; field chrome (padding, caret) is outside it.
      outsidePress: (event: MouseEvent) =>
        !inputRef.current
          ?.closest('.eds-select')
          ?.contains(event.target as Node),
    });
    const role = useRole(context, { role: 'listbox' });

    const triggerRef = useMergeRefs([
      refs.setReference,
      forwardedRef,
      inputRef,
    ]);

    /**
     * The selection's options as last seen, since server results replace
     * `options` on every query and would otherwise take the chips with them.
     * Sync callers never write it: with a stable list, a value whose option the
     * caller removed should stop showing rather than linger.
     */
    const lastKnownOptionsRef = useRef<OptionType<V>[]>([]);

    const selectedOptions = useMemo(
      () =>
        resolveSelectedOptions(
          allOptions,
          lastKnownOptionsRef.current,
          getSelectedValues(props),
        ),
      [allOptions, props],
    );

    useEffect(() => {
      if (isServerFiltered) {
        lastKnownOptionsRef.current = selectedOptions;
      }
    }, [isServerFiltered, selectedOptions]);

    const hasValue = selectedOptions.length > 0;
    const showSelectedLabel = !isMultiSelect(props) && hasValue && !query;
    const showPlaceholder = !hasValue && !query;

    const handleSelectOption = useStableCallback((option: OptionType<V>) => {
      if (!onSelectOption(option)) return;

      const hadQuery = queryRef.current !== '';
      setQueryState('');
      if (!isMultiSelect(props)) {
        handleOpenChange(false);
        return;
      }
      if (!hadQuery) return;
      if (isServerFiltered) {
        setActiveIndex(null);
        return;
      }
      const fullIndex = allOptions.findIndex(({ id }) => id === option.id);
      setActiveIndex(fullIndex === -1 ? null : fullIndex);
    });

    const handleAddOption = useCallback(() => {
      if (!canAddOption) return;

      onAddOption?.(trimmedQuery);
      setQueryState('');
      setActiveIndex(null);
      setIsKeyboardModality(false);

      if (!isMultiSelect(props)) {
        handleOpenChange(false);
      }
    }, [
      canAddOption,
      handleOpenChange,
      onAddOption,
      props,
      setActiveIndex,
      setIsKeyboardModality,
      setQueryState,
      trimmedQuery,
    ]);

    // `isLoading` bars a page too: the list it would extend is being replaced,
    // so the next page belongs to the query on its way out.
    const handleLoadMore = useCallback(() => {
      if (!onLoadMore || isLoading || isLoadingMore) return;
      onLoadMore();
    }, [isLoading, isLoadingMore, onLoadMore]);

    const handleDismissChip = useCallback(
      (optionValue: V) => {
        if (!isMultiSelect(props) || disabled) return;
        props.onChange(
          props.value.filter((value) => !isEqual(value, optionValue)),
        );
      },
      [disabled, props],
    );

    // Navigation and ids read off this, in render order.
    const footerRows: ActionRow[] = [];
    if (canAddOption) {
      footerRows.push({
        id: `${listId}-add-option`,
        text: addOptionText,
        onSelect: handleAddOption,
      });
    }
    if (canLoadMore) {
      footerRows.push({
        id: `${listId}-load-more`,
        isLoading: Boolean(isLoading) || Boolean(isLoadingMore),
        text: loadMoreText,
        onSelect: handleLoadMore,
      });
    }

    const activeActionRow =
      activeIndex == null
        ? undefined
        : footerRows[activeIndex - filteredOptions.length];

    const listNavigation = useListNavigation(context, {
      ...listNavigationOptions,
      disabledIndices: getListboxDisabledIndices({
        footerRows,
        options: filteredOptions,
      }),
      enabled: !disabled,
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([
      dismiss,
      role,
      listNavigation,
    ]);

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      const nextQuery = event.target.value;
      setQueryState(nextQuery);
      setActiveIndex(0);
      setIsKeyboardModality(false);
      if (!isOpen) {
        handleOpenChange(true);
      }
    };

    const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;
      markKeyboardInput();

      if (
        event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'Home' ||
        event.key === 'End'
      ) {
        setIsKeyboardModality(true);
      }

      if (
        !isOpen &&
        (event.key === 'ArrowDown' ||
          event.key === 'ArrowUp' ||
          event.key === 'Enter' ||
          event.key === ' ')
      ) {
        event.preventDefault();
        setIsKeyboardModality(true);
        handleOpenChange(true);
        return;
      }

      if (event.key === 'Enter' && isOpen && activeIndex != null) {
        if (activeActionRow) {
          event.preventDefault();
          activeActionRow.onSelect();
          return;
        }
        const option = filteredOptions[activeIndex];
        if (option) {
          event.preventDefault();
          handleSelectOption(option);
        }
      }
    };

    const handleSelectPointerDown = (event: MouseEvent<HTMLElement>) => {
      if (disabled) return;
      const target = event.target as HTMLElement;
      if (target.closest('.eds-chip--dismiss')) return;
      // Caret toggles open/closed itself.
      if (target.closest('.eds-select--caret')) return;

      if (target !== inputRef.current) {
        // Keep focus on the combobox when clicking field chrome (div is not a
        // label). Cancelling on the input itself would suppress the browser's
        // text cursor placement and drag selection.
        event.preventDefault();
        inputRef.current?.focus();
      }
      if (!isOpen) {
        handleOpenChange(true);
      }
    };

    const handleCaretMouseDown = (event: MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      event.preventDefault();
      setIsKeyboardModality(false);
      inputRef.current?.focus();
      handleOpenChange(!isOpen);
    };

    const activeOption =
      activeIndex == null ? undefined : filteredOptions[activeIndex];
    let activeOptionId: string | undefined;
    if (isOpen && activeActionRow) {
      activeOptionId = activeActionRow.id;
    } else if (isOpen && activeOption) {
      activeOptionId = `${listId}-option-${activeOption.id}`;
    }

    return (
      <>
        <div
          className={clsx(
            'eds-select',
            disabled && 'eds-select--disabled',
            seamless && 'eds-select--seamless',
            status && `eds-select--${status}`,
            isOpen && 'eds-select--open',
          )}
          ref={refs.setPositionReference}
          onPointerDownCapture={handleSelectPointerDown}
        >
          <div className="eds-select--value">
            {isMultiSelect(props) && hasValue ? (
              <span className="eds-select--chips">
                {selectedOptions.map((option) => (
                  <span className="eds-select--chip" key={option.id}>
                    <Chip
                      disabled={disabled}
                      label={option.text}
                      startIcon={option.icon}
                      onDismiss={() => handleDismissChip(option.value)}
                    />
                  </span>
                ))}
              </span>
            ) : null}
            {showSelectedLabel ? (
              <div className="eds-select--selected" aria-hidden="true">
                <TextBlock
                  icon={selectedOptions[0]?.icon}
                  text={selectedOptions[0]?.text}
                  truncate={1}
                  variant="body-m"
                />
              </div>
            ) : null}
            {showPlaceholder ? (
              <span className="eds-select--placeholder" aria-hidden="true">
                <Text tone="subdued" truncate={1} variant="body-m">
                  {placeholder ?? ''}
                </Text>
              </span>
            ) : null}
            <input
              {...getReferenceProps({
                onChange: handleInputChange,
                onKeyDown: handleInputKeyDown,
                ref: triggerRef,
                value: query,
              })}
              aria-activedescendant={activeOptionId}
              aria-autocomplete="list"
              aria-controls={isOpen ? listId : undefined}
              aria-describedby={ariaDescribedBy}
              aria-disabled={disabled || undefined}
              aria-expanded={isOpen}
              aria-haspopup="listbox"
              aria-invalid={status === 'critical' || undefined}
              aria-label={ariaLabel}
              aria-labelledby={ariaLabelledBy}
              aria-required={required || undefined}
              autoComplete="off"
              className={clsx(
                'eds-select--input',
                (showSelectedLabel || showPlaceholder) &&
                  'eds-select--input-overlay',
              )}
              disabled={disabled}
              id={id}
              role="combobox"
              spellCheck={false}
              type="text"
            />
          </div>
          <button
            aria-hidden="true"
            className="eds-select--caret"
            disabled={disabled}
            tabIndex={-1}
            type="button"
            onMouseDown={handleCaretMouseDown}
          >
            <Icon icon={isLoading ? 'loading' : 'caret-down'} size="s" />
          </button>
        </div>
        <span className="eds-styles--visually-hidden" role="status">
          {isOpen && showLoadingPlaceholders
            ? intl.formatMessage(messages.loading)
            : ''}
        </span>
        {isMultiSelect(props) ? (
          props.value.map((value) => (
            <input
              key={stringifyOptionValue(value)}
              name={name}
              type="hidden"
              value={stringifyOptionValue(value)}
            />
          ))
        ) : (
          <input
            name={name}
            type="hidden"
            value={props.value == null ? '' : stringifyOptionValue(props.value)}
          />
        )}
        {isOpen && (
          <FloatingPortal root={portalRoot}>
            <FloatingFocusManager
              context={context}
              initialFocus={-1}
              modal={false}
              returnFocus
            >
              <Listbox
                activeIndex={activeIndex}
                footerRows={footerRows}
                groups={filteredGroups}
                isEmpty={!isLoading && !hasMenuContent}
                isKeyboardModality={isKeyboardModality}
                isLoading={isLoading}
                isLoadingOptions={showLoadingPlaceholders}
                isOptionSelected={isOptionSelected}
                label={ariaLabel}
                multiple={isMultiSelect(props)}
                noResultsText={noResultsText}
                ref={refs.setFloating}
                onHoverIndex={onHoverIndex}
                onSelectOption={handleSelectOption}
                __private={{
                  ariaLabelledBy,
                  elementsRef,
                  floatingProps: getFloatingProps({
                    onKeyDown: markKeyboardInput,
                    onPointerMove: markPointerInput,
                  }),
                  id: listId,
                  style: floatingStyles,
                }}
              />
            </FloatingFocusManager>
          </FloatingPortal>
        )}
      </>
    );
  }),
);

Select.displayName = 'Select';
